import { useRef, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Link, Undo, Redo, AlignLeft, AlignCenter } from 'lucide-react';

function ToolbarButton({ icon: Icon, command, value, title }) {
  const exec = () => { document.execCommand(command, false, value || null); };
  return (
    <button type="button" onClick={exec} title={title}
      className="w-8 h-8 rounded flex items-center justify-center text-white/40 hover:bg-white/[0.08] hover:text-white transition-colors"
      data-testid={`editor-btn-${command}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

export default function RichTextEditor({ value, onChange, className = '' }) {
  const editorRef = useRef(null);

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) document.execCommand('createLink', false, url);
  };

  return (
    <div className={`border border-white/[0.08] rounded-xl overflow-hidden ${className}`} data-testid="rich-text-editor">
      <div className="flex items-center gap-0.5 p-2 bg-[#191919] border-b border-white/[0.08] flex-wrap">
        <ToolbarButton icon={Bold} command="bold" title="Bold" />
        <ToolbarButton icon={Italic} command="italic" title="Italic" />
        <ToolbarButton icon={Underline} command="underline" title="Underline" />
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <ToolbarButton icon={Heading1} command="formatBlock" value="h2" title="Heading" />
        <ToolbarButton icon={Heading2} command="formatBlock" value="h3" title="Subheading" />
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <ToolbarButton icon={List} command="insertUnorderedList" title="Bullet List" />
        <ToolbarButton icon={ListOrdered} command="insertOrderedList" title="Numbered List" />
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <ToolbarButton icon={AlignLeft} command="justifyLeft" title="Align Left" />
        <ToolbarButton icon={AlignCenter} command="justifyCenter" title="Align Center" />
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <button type="button" onClick={insertLink} title="Insert Link"
          className="w-8 h-8 rounded flex items-center justify-center text-white/40 hover:bg-white/[0.08] hover:text-white transition-colors"
          data-testid="editor-btn-link">
          <Link className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <ToolbarButton icon={Undo} command="undo" title="Undo" />
        <ToolbarButton icon={Redo} command="redo" title="Redo" />
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        dangerouslySetInnerHTML={{ __html: value || '' }}
        onInput={handleInput}
        className="min-h-[300px] p-4 bg-[#1a1a1a] text-sm text-white/70 focus:outline-none [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h3]:font-heading [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white/80 [&_a]:text-gold [&_a]:underline [&_li]:ml-4"
        data-testid="rich-text-content"
      />
    </div>
  );
}
